// ABOUTME: Instanced billboard grass blades for the foreground band silhouette
// ABOUTME: Random per-instance yaw + tint + wind phase, single draw call

import * as THREE from 'three';

export const GRASS_NAME = 'hopp-grass-billboards';
export const MAX_GRASS_INSTANCES = 500000;

function smoothstep(edge0, edge1, x) {
    if (edge0 === edge1) return x < edge0 ? 0 : 1;
    const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
    return t * t * (3 - 2 * t);
}

const GRASS_VERT = /* glsl */ `
    attribute vec3 aTint;
    attribute float aPhase;

    uniform float uTime;
    uniform float uSwayAmount;
    uniform float uSwaySpeed;

    varying vec3 vTint;
    varying float vY;
    varying vec3 vWorldPos;

    void main() {
        vTint = aTint;
        vY = position.y;

        // Bend top of blade with wind. Bottom (y=0) stays planted; tip (y=1) sways most.
        float t = uTime * uSwaySpeed + aPhase;
        float sway = sin(t) * uSwayAmount * vY * vY;
        vec3 swayed = position + vec3(sway, 0.0, 0.0);

        vec4 world = modelMatrix * instanceMatrix * vec4(swayed, 1.0);
        vWorldPos = world.xyz;
        gl_Position = projectionMatrix * viewMatrix * world;
    }
`;

const GRASS_FRAG = /* glsl */ `
    precision mediump float;

    uniform vec3 uBaseColor;
    uniform vec3 uTipColor;
    uniform vec3 uFogColor;
    uniform vec3 uLightTint;
    uniform float uFogNear;
    uniform float uFogFar;
    uniform float uOpacity;

    varying vec3 vTint;
    varying float vY;
    varying vec3 vWorldPos;

    void main() {
        vec3 col = mix(uBaseColor, uTipColor, smoothstep(0.0, 1.0, vY));
        col += vTint;

        // Modulate by the time-of-day light tint so blades darken at night
        // and pick up the moon's cool colour, matching the pitch shader.
        col *= uLightTint;

        // Match the pitch shader's atmospheric haze so distant blades fade to
        // the same sky colour as the flat ground beneath them.
        float dist = length(vWorldPos.xz);
        float fog = smoothstep(uFogNear, uFogFar, dist);
        col = mix(col, uFogColor, fog);

        gl_FragColor = vec4(col, uOpacity);
    }
`;

/**
 * Build a foreground band of grass billboards aligned with the initial camera
 * forward direction. Each instance is a thin upright quad with a random yaw,
 * height jitter, colour tint, and wind phase.
 */
export function createGrassBillboards({
    cameraPose,
    bandWidth = 240,
    bandLength = 260,
    bandOffsetForward = -12,
    density = 10,
    bladeWidth = 0.03,
    bladeHeight = 0.15,
    bladeHeightJitter = 0.45,
    edgeFadeForward = 0.18,
    edgeFadeSide = 0.22,
    baseColor = 0x2f9933,
    tipColor = 0x6cc566,
    tintJitter = 0.08,
    swayAmount = 0.06,
    swaySpeed = 1.4,
    fogColor = 0x9cb6c8,
    fogNear = 150,
    fogFar = 1500,
    maxInstances = MAX_GRASS_INSTANCES,
} = {}) {
    const area = bandWidth * bandLength;
    const cappedMax = Math.max(1, Math.floor(maxInstances));
    const count = Math.max(1, Math.min(cappedMax, Math.floor(area * density)));

    const geometry = new THREE.PlaneGeometry(bladeWidth, 1, 1, 1);
    // Anchor the blade at its base so instance scale.y stretches the tip upward only.
    geometry.translate(0, 0.5, 0);

    const aTint = new THREE.InstancedBufferAttribute(new Float32Array(count * 3), 3);
    const aPhase = new THREE.InstancedBufferAttribute(new Float32Array(count), 1);
    geometry.setAttribute('aTint', aTint);
    geometry.setAttribute('aPhase', aPhase);

    const material = new THREE.ShaderMaterial({
        uniforms: {
            uTime:       { value: 0 },
            uSwayAmount: { value: swayAmount },
            uSwaySpeed:  { value: swaySpeed },
            uBaseColor:  { value: new THREE.Color(baseColor) },
            uTipColor:   { value: new THREE.Color(tipColor) },
            uFogColor:   { value: new THREE.Color(fogColor) },
            uLightTint:  { value: new THREE.Color(1, 1, 1) },
            uFogNear:    { value: fogNear },
            uFogFar:     { value: fogFar },
            uOpacity:    { value: 1.0 },
        },
        vertexShader: GRASS_VERT,
        fragmentShader: GRASS_FRAG,
        side: THREE.DoubleSide,
        transparent: false,
        depthWrite: true,
    });

    const mesh = new THREE.InstancedMesh(geometry, material, count);
    mesh.name = GRASS_NAME;
    mesh.frustumCulled = false;

    const camPos = cameraPose?.position?.clone() ?? new THREE.Vector3(0, 15, 50);
    const target = cameraPose?.target?.clone() ?? new THREE.Vector3(0, 0, 0);
    const forward = target.clone().sub(camPos);
    forward.y = 0;
    if (forward.lengthSq() < 1e-6) forward.set(0, 0, -1);
    forward.normalize();
    const right = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), forward).normalize();

    const bandStart = camPos.clone();
    bandStart.y = 0;
    bandStart.add(forward.clone().multiplyScalar(bandOffsetForward));

    const dummy = new THREE.Object3D();
    for (let i = 0; i < count; i++) {
        const u = Math.random();
        const v = Math.random() - 0.5;

        // Shrink blades toward the band edges so the silhouette feathers into
        // the flat pitch shader instead of cutting off as a rectangle.
        const forwardFade = smoothstep(0, edgeFadeForward, u)
            * (1 - smoothstep(1 - edgeFadeForward, 1, u));
        const sideFade = 1 - smoothstep(0.5 - edgeFadeSide, 0.5, Math.abs(v));
        // Per-blade noise on the fade pushes the boundary off the grid so the
        // edge reads as scattered tufts, not a clean falloff line.
        const fade = forwardFade * sideFade * (0.55 + Math.random() * 0.45);

        const heightScale = bladeHeight
            * (1 + (Math.random() - 0.5) * 2 * bladeHeightJitter)
            * fade;
        const widthScale = 0.85 + Math.random() * 0.4;
        const yaw = Math.random() * Math.PI * 2;

        const pos = bandStart.clone()
            .add(forward.clone().multiplyScalar(u * bandLength))
            .add(right.clone().multiplyScalar(v * bandWidth));

        dummy.position.copy(pos);
        dummy.rotation.set(0, yaw, 0);
        dummy.scale.set(widthScale, heightScale, 1);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);

        aTint.setXYZ(
            i,
            (Math.random() - 0.5) * tintJitter * 0.4,
            (Math.random() - 0.5) * tintJitter,
            (Math.random() - 0.5) * tintJitter * 0.4,
        );
        aPhase.setX(i, Math.random() * Math.PI * 2);
    }

    mesh.instanceMatrix.needsUpdate = true;
    aTint.needsUpdate = true;
    aPhase.needsUpdate = true;

    return mesh;
}

export function updateGrassWind(grassMesh, timeSeconds) {
    const u = grassMesh?.material?.uniforms;
    if (!u?.uTime) return;
    u.uTime.value = timeSeconds;
}

/**
 * Apply the time-of-day lighting state to the grass material. Blades have no
 * real lighting model — we just multiply the final colour by a tint roughly
 * equivalent to "primary * intensity + ambient * intensity" so they track the
 * pitch's brightness through dusk and into night.
 */
export function setGrassLighting(grassMesh, state) {
    const tint = grassMesh?.material?.uniforms?.uLightTint;
    if (!tint || !state) return;
    const r = state.primaryColor.r * state.primaryIntensity * 0.65
            + state.ambientColor.r * state.ambientIntensity * 0.55;
    const g = state.primaryColor.g * state.primaryIntensity * 0.65
            + state.ambientColor.g * state.ambientIntensity * 0.55;
    const b = state.primaryColor.b * state.primaryIntensity * 0.65
            + state.ambientColor.b * state.ambientIntensity * 0.55;
    tint.value.setRGB(r, g, b);
}
